/**
 * NutritionInfoModal — bottom-sheet-style modal used by the Nutrition Scan
 * results screen for two purposes:
 *
 *   1. "Learn more" — the elegant Apple-compliant disclaimer expanded.
 *   2. "Sources & References" — tappable authoritative links.
 *
 * Implementation notes for Build 14:
 *   • Rendered via React Portal into document.body so it escapes any parent
 *     stacking context / overflow-hidden / transform (the Build 13 bug where
 *     the overlay showed but the sheet did not appear on iOS Capacitor was
 *     caused by rendering it inside the scrolling scanner screen container).
 *   • z-index is bumped well above the bottom nav (999) and above the app
 *     toasts (9500).
 *   • iPhone home-indicator safe area respected via
 *     `env(safe-area-inset-bottom)` padding.
 *   • Overlay tap closes; ESC key closes; body-scroll is locked while open.
 *
 * Added for App Store Guideline 1.4.1 compliance while keeping the results
 * screen premium and uncluttered.
 */
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Info, BookOpen, ExternalLink } from "lucide-react";

const SOURCES = [
  {
    label: "USDA FoodData Central",
    detail: "Calorie and macronutrient values",
    url: "https://fdc.nal.usda.gov",
  },
  {
    label: "NIH Dietary Reference Intakes",
    detail: "Protein, carbohydrate, fat, and water recommendations",
    url: "https://ods.od.nih.gov/HealthInformation/dietary_reference_intakes.aspx",
  },
  {
    label: "WHO Healthy Diet",
    detail: "Global healthy-diet guidelines",
    url: "https://www.who.int/news-room/fact-sheets/detail/healthy-diet",
  },
  {
    label: "Academy of Nutrition and Dietetics",
    detail: "General healthy-eating guidance",
    url: "https://www.eatright.org",
  },
  {
    label: "PubMed — Mifflin–St Jeor Equation",
    detail: "Basal metabolic rate formula used to estimate daily calories",
    url: "https://pubmed.ncbi.nlm.nih.gov/2305711/",
  },
];

export default function NutritionInfoModal({ open, mode = "disclaimer", onClose }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  const node = (
    <div
      data-testid={`nutrition-info-modal-${mode}`}
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0, left: 0, right: 0, bottom: 0,
        background: "rgba(0,0,0,0.72)",
        WebkitBackdropFilter: "blur(10px)",
        backdropFilter: "blur(10px)",
        zIndex: 10000,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        padding: 0,
        animation: "nutriModalFade 220ms ease-out",
      }}
    >
      <style>{`
        @keyframes nutriModalFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes nutriModalSlide { from { transform: translateY(32px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
      `}</style>

      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 520,
          maxHeight: "84vh",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          background: "var(--bg-elev-1, #141414)",
          borderTop: "1px solid rgba(178,138,62,0.35)",
          borderRadius: "22px 22px 0 0",
          padding: "22px 22px calc(32px + env(safe-area-inset-bottom, 0px)) 22px",
          animation: "nutriModalSlide 280ms cubic-bezier(.2,.9,.3,1)",
          boxShadow: "0 -30px 60px rgba(0,0,0,0.6)",
        }}
      >
        {/* Drag handle */}
        <div
          style={{
            width: 40, height: 4, borderRadius: 2,
            background: "rgba(255,255,255,0.14)",
            margin: "0 auto 18px",
          }}
        />

        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {mode === "disclaimer" ? (
              <Info size={16} strokeWidth={1.8} color="var(--gold, #b28a3e)" />
            ) : (
              <BookOpen size={16} strokeWidth={1.8} color="var(--gold, #b28a3e)" />
            )}
            <div style={{ fontSize: 17, fontWeight: 600, color: "var(--text)", fontFamily: "'Fraunces', serif" }}>
              {mode === "disclaimer" ? "Nutrition Estimates" : "Sources & References"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid={`nutrition-info-modal-${mode}-close`}
            style={{
              background: "rgba(255,255,255,0.05)",
              border: 0,
              width: 30, height: 30,
              borderRadius: 15,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer",
              color: "var(--text-dim)",
              flexShrink: 0,
            }}
          >
            <X size={14} strokeWidth={1.8} />
          </button>
        </div>

        {mode === "disclaimer" ? (
          <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 14 }}>
            <p style={{ fontSize: 14, lineHeight: 1.6, color: "var(--text-dim)", margin: 0 }}>
              Nutrition estimates are AI-generated and may vary based on image
              quality and food recognition.
            </p>
            <p style={{ fontSize: 14, lineHeight: 1.6, color: "var(--text-dim)", margin: 0 }}>
              This information is provided for educational and informational
              purposes only and should not be considered medical advice.
            </p>
            <p style={{ fontSize: 14, lineHeight: 1.6, color: "var(--text-dim)", margin: 0 }}>
              Consult a licensed healthcare provider or registered dietitian
              before making significant dietary changes.
            </p>
          </div>
        ) : (
          <ul
            data-testid="nutrition-sources-list"
            style={{ listStyle: "none", padding: 0, margin: "16px 0 0 0", display: "flex", flexDirection: "column", gap: 10 }}
          >
            {SOURCES.map((s) => (
              <li key={s.url}>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`nutrition-source-${s.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    padding: "16px 18px",
                    borderRadius: 14,
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.06)",
                    textDecoration: "none",
                    transition: "background 160ms",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                    <span style={{ color: "var(--text)", fontSize: 14.5, fontWeight: 500 }}>
                      {s.label}
                    </span>
                    <span style={{ color: "var(--text-faint)", fontSize: 12, lineHeight: 1.45 }}>{s.detail}</span>
                  </div>
                  <ExternalLink size={15} strokeWidth={1.8} color="var(--gold, #b28a3e)" style={{ flexShrink: 0 }} />
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  // Portal renders the modal into document.body, which escapes any parent
  // that has overflow-hidden, transform, or filter set (which was creating
  // a new stacking context and swallowing the sheet on iOS in Build 13).
  return createPortal(node, document.body);
}
